import { createLogic } from "redux-logic";
import type { BuildAndBuyLogicType } from "../store";
import {
    type TradeInAemClearExtData,
    type TradeInAemSetExtData,
    type TradeInAemSetIncompatible,
    type TradeInAemSetNotification,
    setTradeInNotification,
    TRADE_IN_AEM_CLEAR_EXT_DATA,
    TRADE_IN_AEM_SET_EXT_DATA,
    TRADE_IN_AEM_SET_INCOMPATIBLE,
    TRADE_IN_AEM_SET_NOTIFICATION,
} from "../../../../../common-deprecated/features/tradein/redux/actions/TradeInAemExternalDataActions";
import { startFinanceRateFetch } from "../actions/FinanceInsuranceActions";
import { NavStep } from "../../../shared-logic/utils/Navigation";
import { startConfigSave } from "../actions/SaveActions";
import { getTradeInCashNotificationConfig } from "../../../../../common-deprecated/features/tradein/utils/tradeInAemUtils";
import * as SessionStorage from "../../../../../common-deprecated/SessionStorage";
import { FinanceUpdateTrigger } from "../../../../../common-deprecated/features/tradein/utils/tradeInAemUtilTypes";
import { getBnbLabel } from "../../utils/labels";
import { addNotification } from "../../../../../common-deprecated/redux/actions/NotificationActions";

const tradeInAemExternalDataLogic = (createLogic as BuildAndBuyLogicType<TradeInAemSetExtData>)({
    type: [TRADE_IN_AEM_SET_EXT_DATA],

    process({ getState }, dispatch, done) {
        const { tradeInAemExternalData, basket, navigation, save } = getState();

        if (tradeInAemExternalData.data?.vehicleValue) {
            // Trigger finance fetch.
            dispatch(startFinanceRateFetch(FinanceUpdateTrigger.PxAdded));

            // Don't need a finance rate fetch to calculate the cash notification.
            const config = getTradeInCashNotificationConfig(
                tradeInAemExternalData.data.vehicleValue,
                Number(basket.total.discount),
                tradeInAemExternalData.notification.cash.notificationType,
                tradeInAemExternalData.notification.cash.show,
            );

            // Update the configuration. As we don't save by default on customize, only update if it is already saved
            dispatch(setTradeInNotification("cash", config.show, config.notificationType, config.isError));

            if (navigation.step === NavStep.Summary || save.data) dispatch(startConfigSave());
        }

        done();
    },
});

const tradeInAemClearExternalDataLogic = (createLogic as BuildAndBuyLogicType<TradeInAemClearExtData>)({
    type: [TRADE_IN_AEM_CLEAR_EXT_DATA],

    process(_, dispatch, done) {
        dispatch(startFinanceRateFetch(FinanceUpdateTrigger.PxRemoved));
        dispatch(setTradeInNotification("cash", false));
        dispatch(setTradeInNotification("monthly", false));
        dispatch(startConfigSave());

        done();
    },
});

const tradeInAemSetNotificationLogic = (createLogic as BuildAndBuyLogicType<TradeInAemSetNotification>)({
    type: TRADE_IN_AEM_SET_NOTIFICATION,

    validate({ getState, action }, allow) {
        const { navigation, tradeInAemExternalData } = getState();
        const { show, notificationType } = action;
        // Do not show the message if the message has already been shown in a previous step. See OR-1326.
        if (show) {
            const vehicleName = tradeInAemExternalData.data?.vehicleName || "";
            const sessionData = { step: navigation.step, notificationType, vehicleName };
            const previousTradeInNotification = SessionStorage.get("previousTradeInNotification");
            const prevSessionData = previousTradeInNotification ? JSON.parse(previousTradeInNotification) : false;
            SessionStorage.set("previousTradeInNotification", JSON.stringify(sessionData));

            if (
                prevSessionData &&
                prevSessionData.step !== navigation.step &&
                prevSessionData.notificationType === notificationType &&
                prevSessionData.vehicleName === vehicleName
            ) {
                action.show = false;
            }
        }

        allow(action);
    },
});

const tradeInAemSetIncompatibleLogic = (createLogic as BuildAndBuyLogicType<TradeInAemSetIncompatible>)({
    type: TRADE_IN_AEM_SET_INCOMPATIBLE,

    async process({ getState, action }, dispatch, done) {
        const { tradeInAemExternalData, commonSettings, bnbSettings, utils } = getState();

        if (tradeInAemExternalData.data.quoteId) {
            const [tradeinBasketUpdateLabel, tradeinBasketAddedLabel, tradeInBasketRefusedLabel] = getBnbLabel(
                { commonSettings, bnbSettings },
                [
                    "tradeinBasketUpdate",
                    utils.financeOption === "monthly" ? "tradeinBasketAdded" : "tradeinBasketAddedCash",
                    "tradeInBasketRefusedFinanceProduct",
                ],
            );

            dispatch(
                addNotification([
                    {
                        title: tradeinBasketUpdateLabel,
                        description:
                            !tradeInAemExternalData.incompatible && !action.incompatible
                                ? tradeinBasketAddedLabel
                                : tradeInBasketRefusedLabel,
                    },
                ]),
            );
        }

        done();
    },
});

export default [
    tradeInAemExternalDataLogic,
    tradeInAemClearExternalDataLogic,
    tradeInAemSetNotificationLogic,
    tradeInAemSetIncompatibleLogic,
];
